import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body2 } from '@ui/Typography'
import { Link } from '@ui/components/Link'
import { Modal } from '@ui/components/Modal'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'

import { Box } from '@shared/components/Box'

interface TradingPoolCampaignAboutModalProps {
  campaignId: string
}

export const TradingPoolCampaignAboutModal: BaseModalType<TradingPoolCampaignAboutModalProps> = ({
  campaignId,
  close,
}) => {
  const { t } = useTranslation()

  const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Box flex align='center' gap='x8' py='x2'>
      <Box flexItem='1 0 0'>
        <Body2 color='text-low-em'>{label}</Body2>
      </Box>
      <Box flexItem='1 0 0'>
        <Body2 color='text-high-em'>{children}</Body2>
      </Box>
    </Box>
  )
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.campaign.tradingPoolDetailAboutModal(campaignId),
    queryFn: () => apiClient.getTradingPoolCampaignDetailsAboutModal(campaignId, locale),
    enabled: !!campaignId,
  })

  return (
    <Modal>
      <Modal.Header>{t('RewardsHub.TradingPool.About the Trading Pool')}</Modal.Header>
      <Modal.Body isLoading={isLoading}>
        <Modal.Body.Text>
          <Modal.Body.Text color='text-high-em'>
            {t('RewardsHub.TradingPool.Modal Description')}
          </Modal.Body.Text>
        </Modal.Body.Text>

        <Box flex gap={'x16'} direction={'column'} fullWidth>
          <InfoRow label={t('RewardsHub.TradingPool.Campaign Period')}>
            {formatDate(data?.startDate ?? '', {
              formatType: 'dd MMMM yyyy',
              withTime: false,
            })}{' '}
            -{' '}
            {formatDate(data?.endDate ?? '', {
              formatType: 'dd MMMM yyyy',
              withTime: false,
            })}
          </InfoRow>
          <InfoRow label={t('RewardsHub.TradingPool.Instrument')}>
            {data?.instruments.map((instrument) => instrument).join(', ')}
          </InfoRow>
          <InfoRow label={t('RewardsHub.TradingPool.Entry rule')}>
            {data?.entriesPerLot} {t('RewardsHub.TradingPool.raffle(s) entry per 1.0 lot traded')}
          </InfoRow>
          <InfoRow label={t('RewardsHub.TradingPool.Pool Growth')}>
            ${data?.contributionPerLotInUsd}{' '}
            {t('RewardsHub.TradingPool.added per 1.0 lot traded by community')}
          </InfoRow>
          <InfoRow label={t('RewardsHub.TradingPool.Winners')}>
            {data?.winnersCount} {t('RewardsHub.TradingPool.unique winner(s) share the pool')}
          </InfoRow>
          <InfoRow label={t('RewardsHub.TradingPool.Prize Rule')}>
            {t('RewardsHub.TradingPool.Max one prize per participant')}
          </InfoRow>
          <Link href={data?.termsAndConditionsUrl}>
            {t('RewardsHub.TradingPool.Terms & Conditions')}
          </Link>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Close')}</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}
